import React, {useEffect, useState} from 'react';
import {Formik} from 'formik';
import {useRouteMatch} from 'react-router-dom';
import {checkRequiredValues} from 'common/utils/form/checkRequiredValues';
import {normalizeFormValues} from 'common/utils/form/normalizeFormValues';
import {pathnames as P} from 'common/constants/pathnames';
import {fileTypeId as T} from '../../constants/fileTypeId';
import {isFileUploaded} from '../../utils/isFileUploaded';
import SilentSubmitHandler from '../SilentSubmitHandler';
import ModalPreviewDocument from '../../../../common/components/ModalPreviewDocument';
import Form_6View from './Form_6-view';
import {
  buildPermitReasonOptions,
  supervisionFileOptions,
  exceedUsePermitOptions,
  exceedBuildingOptions,
  form4Options,
  completeCertOptions,
  dangerousStructureOptions
} from '../../../../common/constants/options';
import {uploadingProgress} from '../../../../common/utils/form/uploadingProgress';
import {
  getSharedAndPrivateFiles,
  filterFileListByFileTypeId,
  getFileFolderID
} from '../../utils/fileHelpers';
import ButtonSubmitForm from '../ButtonSubmitForm';
import SpinnerContainer from '../../../../common/components/SpinnerContainer';
import {getOption} from "../../utils/getOption";

const Form_6 = ({
                  submitStep,
                  stepData,
                  uploadFile,
                  getShumaStepStatus,
                  showGlobalErrorMessage,
                  getStepData,
                  shumaTypeID,
                  getFileList,
                  filesListData
                }) => {
  const [reinit, setReinit] = useState(true);
  const [progress, setProgress] = useState(0);
  const [showUploadingFilePopUp, setShowUploadingFilePopUp] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {getFileList({shumaId})}, []);

  const {
    fileList,
    fileUploaded,
    supervisionFile,
    exceedUsePermit,
    exceedBuilding,
    buildPermitDate,
    buildPermitReason,
    buildPermitNotSigned,
    form4,
    form4Text,
    completeCert,
    completeCertText,
    dangerousStructure,
    dangerousStructureText,
    plansCheckDate,
    ...rest
  } = stepData;

  const initialValues = {
    plansCheckDate: (plansCheckDate) ? new Date(plansCheckDate) : null,
    validLocalPlansDetails: '',
    countryStateData: '',
    plansInProgress: '',
    buildPermitNumber: '',
    buildPermitDate: (buildPermitDate) ? new Date(buildPermitDate) : null,
    buildPermitReason: getOption(buildPermitReasonOptions, buildPermitReason),
    buildPermitDetails: '',
    buildPermitNotSigned: Boolean(buildPermitNotSigned) || null,
    buildPermitNotSignedText: '',
    additionalBuildPermitText: '',
    supervisionFile: getOption(supervisionFileOptions, supervisionFile),
    exceedUsePermit: getOption(exceedUsePermitOptions,exceedUsePermit),
    exceedBuilding: getOption(exceedBuildingOptions, exceedBuilding),
    housePermitMismatchText: '',
    form4: getOption(form4Options, form4),
    form4Text: form4Text || '',
    completeCert: getOption(completeCertOptions, completeCert),
    completeCertText: completeCertText || '',
    dangerousStructure: getOption(dangerousStructureOptions, dangerousStructure),
    dangerousStructureText: dangerousStructureText || '',

    fileType12Switch: false,
    fileType13Switch: false,
    fileType14Switch: false,
    fileType15Switch: false,
    fileType16Switch: false,
    fileType22Switch: false,
    fileType23Switch: false,
    fileType24Switch: false,
    fileType25Switch: false,
    fileType32Switch: false,

    ...rest
  };

  const commonHousePlanFileUploaded = isFileUploaded({
    fileList: filesListData,
    fileTypeID: T.COMMON_HOUSE_PLAN_LOCATING_APARTMENT
  });
  const permitBlueprintFileUploaded = isFileUploaded({
    fileList: filesListData,
    fileTypeID: T.PERMIT_BLUEPRINT
  });
  const plansInEffectFileUploaded = isFileUploaded({
    fileList: filesListData,
    fileTypeID: T.PLANS_IN_EFFECT
  });
  const licensingDocumentsFileUploaded = isFileUploaded({
    fileList: filesListData,
    fileTypeID: T.LICENSING_DOCUMENTS
  });
  const houseSharedDocumentsFileUploaded = isFileUploaded({
    fileList: filesListData,
    fileTypeID: T.HOUSE_SHARED_DOCUMENTS
  });
  const form4FileUploaded = isFileUploaded({
    fileList: filesListData,
    fileTypeID: T.FORM_4
  });
  const completeCertFileUploaded = isFileUploaded({
    fileList: filesListData,
    fileTypeID: T.COMPLETE_CERT
  });
  const dangerousStructureFileUploaded = isFileUploaded({
    fileList: filesListData,
    fileTypeID: T.DANGEROUS_STRUCTURE
  });
  const houseSharedOrderFileUploaded = isFileUploaded({
    fileList: filesListData,
    fileTypeID: T.HOUSE_SHARED_ORDER
  });
  const propertyLegalDefinitionFileUploaded = isFileUploaded({
    fileList: filesListData,
    fileTypeID: T.PROPERTY_LEGAL_DEFINITION
  });

  const {params: {shumaId, formId: stepId}} = useRouteMatch(P.INSPECTION_AND_FORM_IDS);

  const validate = values => {
    const {
      buildPermitNotSigned,
      buildPermitNotSignedText,
      exceedUsePermit,
      exceedBuilding,
      housePermitMismatchText
    } = values;
    const conditionMandatoryField = ((exceedUsePermit && exceedUsePermit.value === 1) || (exceedBuilding && exceedBuilding.value === 1));

    switch (shumaTypeID) {
      case 1:
      case 2:
      case 3:
      case 4:
        if (conditionMandatoryField) {
          return checkRequiredValues({
            buildPermitNotSigned,
            buildPermitNotSignedText,
            housePermitMismatchText
          }, {});
        } else {
          return checkRequiredValues({
            buildPermitNotSigned,
            buildPermitNotSignedText,
          }, {});
        }
      default:
        return {};
    }
  };

  const submitWithoutFormReinitialization = values => {
    const {
      existingFiles, ...restValues
    } = normalizeFormValues(values);
    const data = {
      shumaID: shumaId,
      shumaTypeID,
      ...restValues
    };

    setReinit(false);  //https://github.com/formium/formik/issues/2369
    return submitStep({step: 6, data})
      .then(() => setReinit(true));
  };
  const handleDataSubmit = (values, shumaId, dirty) => {
    if (dirty) {
      submitWithoutFormReinitialization(values)
        .then(() => getShumaStepStatus({shumaId, resetValues: false}))
        .catch((err) => showGlobalErrorMessage(err.message));
    }
  };

  const handleSubmitForSharedFiles = formikProps => {
    Promise.resolve()
      .then(() => {
        submitWithoutFormReinitialization(formikProps.values);
      })
      .then(() => {
        getFileList({shumaId})
      })
      .then(() => getShumaStepStatus({shumaId, resetValues: false}));
  };

  const fileTypeIds = {
    commonHousePlanLocatingApartment: T.COMMON_HOUSE_PLAN_LOCATING_APARTMENT,
    permitBlueprint: T.PERMIT_BLUEPRINT,
    plansInEffect: T.PLANS_IN_EFFECT,
    licensingDocuments: T.LICENSING_DOCUMENTS,
    houseSharedDocuments: T.HOUSE_SHARED_DOCUMENTS,
    form4: T.FORM_4,
    completeCert: T.COMPLETE_CERT,
    dangerousStructure: T.DANGEROUS_STRUCTURE,
    houseSharedOrder: T.HOUSE_SHARED_ORDER,
    propertyLegalDefinition: T.PROPERTY_LEGAL_DEFINITION,
  };

  const handleFileUpload = formikProps => fileTypeId => (file, payload) => {
    setShowUploadingFilePopUp(true);
    submitFiles(fileTypeId)(file, payload)
      .then(() => uploadingProgress(setProgress))
      .then(() => submitWithoutFormReinitialization(formikProps.values))
      .then(() => getShumaStepStatus({shumaId, resetValues: false}))
      .finally(() => {
        setShowUploadingFilePopUp(false);
        getFileList({shumaId});
        getStepData({shumaId, stepId});
      });
  };

  const submitFiles = fileTypeId => (fileToSave, payload) => {
    return uploadFile({
      shumaId,
      filename: fileToSave.name,
      fileTypeId,
      fileToSave
    }, payload);
  };

  const getFileSelectOptions = formikProps => {
    const filteredSharedFileID12 = filterFileListByFileTypeId(fileList, fileTypeIds.commonHousePlanLocatingApartment);
    const filteredSharedFileID13 = filterFileListByFileTypeId(fileList, fileTypeIds.permitBlueprint);
    const filteredSharedFileID14 = filterFileListByFileTypeId(fileList, fileTypeIds.plansInEffect);
    const filteredSharedFileID15 = filterFileListByFileTypeId(fileList, fileTypeIds.licensingDocuments);
    const filteredSharedFileID16 = filterFileListByFileTypeId(fileList, fileTypeIds.houseSharedDocuments);
    const filteredSharedFileID22 = filterFileListByFileTypeId(fileList, fileTypeIds.form4);
    const filteredSharedFileID23 = filterFileListByFileTypeId(fileList, fileTypeIds.completeCert);
    const filteredSharedFileID24 = filterFileListByFileTypeId(fileList, fileTypeIds.dangerousStructure);
    const filteredSharedFileID25 = filterFileListByFileTypeId(fileList, fileTypeIds.houseSharedOrder);
    const filteredSharedFileID32 = filterFileListByFileTypeId(fileList, fileTypeIds.propertyLegalDefinition);

    const onSubmit = () => handleSubmitForSharedFiles(formikProps);

    return {
      commonHousePlan: {
        type: getSharedAndPrivateFiles(filteredSharedFileID12),
        fileTypeID: fileTypeIds.commonHousePlanLocatingApartment,
        onSubmit,
        folderID: getFileFolderID(filesListData, fileTypeIds.commonHousePlanLocatingApartment)
      },
      permitBlueprint: {
        type: getSharedAndPrivateFiles(filteredSharedFileID13),
        fileTypeID: fileTypeIds.permitBlueprint,
        onSubmit,
        folderID: getFileFolderID(filesListData, fileTypeIds.permitBlueprint)
      },
      plansInEffect: {
        type: getSharedAndPrivateFiles(filteredSharedFileID14),
        fileTypeID: fileTypeIds.plansInEffect,
        onSubmit,
        folderID: getFileFolderID(filesListData, fileTypeIds.plansInEffect)
      },
      licensingDocuments: {
        type: getSharedAndPrivateFiles(filteredSharedFileID15),
        fileTypeID: fileTypeIds.licensingDocuments,
        onSubmit,
        folderID: getFileFolderID(filesListData, fileTypeIds.licensingDocuments)
      },
      houseSharedDocuments: {
        type: getSharedAndPrivateFiles(filteredSharedFileID16),
        fileTypeID: fileTypeIds.houseSharedDocuments,
        onSubmit,
        folderID: getFileFolderID(filesListData, fileTypeIds.houseSharedDocuments)
      },
      form4: {
        type: getSharedAndPrivateFiles(filteredSharedFileID22),
        fileTypeID: fileTypeIds.form4,
        onSubmit,
        folderID: getFileFolderID(filesListData, fileTypeIds.form4)
      },
      completeCert: {
        type: getSharedAndPrivateFiles(filteredSharedFileID23),
        fileTypeID: fileTypeIds.completeCert,
        onSubmit,
        folderID: getFileFolderID(filesListData, fileTypeIds.completeCert)
      },
      dangerousStructure: {
        type: getSharedAndPrivateFiles(filteredSharedFileID24),
        fileTypeID: fileTypeIds.dangerousStructure,
        onSubmit,
        folderID: getFileFolderID(filesListData, fileTypeIds.dangerousStructure)
      },
      houseSharedOrder: {
        type: getSharedAndPrivateFiles(filteredSharedFileID25),
        fileTypeID: fileTypeIds.houseSharedOrder,
        onSubmit,
        folderID: getFileFolderID(filesListData, fileTypeIds.houseSharedOrder)
      },
      propertyLegalDefinition: {
        type: getSharedAndPrivateFiles(filteredSharedFileID32),
        fileTypeID: fileTypeIds.propertyLegalDefinition,
        onSubmit,
        folderID: getFileFolderID(filesListData, fileTypeIds.propertyLegalDefinition)
      },
    };
  };

  const handleDataSubmitForPreview = (values, shumaId, dirty) => {
    if (dirty) {
      submitWithoutFormReinitialization(values)
        .then(() => getStepData({shumaId, stepId}))
        .then(() => getShumaStepStatus({shumaId, resetValues: false}))
        .catch((err) => showGlobalErrorMessage(err.message));
    }
  };

  const handleDataSubmitForSave = (values, shumaId, dirty) => {
    if (dirty) {
      setLoading(true);
      submitWithoutFormReinitialization(values)
        .then(() => getStepData({shumaId, stepId}))
        .then(() => getShumaStepStatus({shumaId, resetValues: false}))
        .catch((err) => showGlobalErrorMessage(err.message))
        .finally(() => setLoading(false));
    }
  };

  return (
    <Formik
      initialValues={initialValues}
      validate={validate}
      onSubmit={() => {
      }}
      enableReinitialize={reinit}
      validateOnChange={true}
      validateOnBlur={false}
    >
      {(formikProps) => (
        <>
          <SilentSubmitHandler
            onSubmit={handleDataSubmit}
            values={formikProps.values}
            shumaId={shumaId}
            dirty={formikProps.dirty}
          />
          <ButtonSubmitForm
            onClick={() => {
              handleDataSubmitForSave(
                formikProps.values,
                shumaId,
                formikProps.dirty
              )
            }}
            disabled={!formikProps.dirty}
          />
          <ModalPreviewDocument
            submitOnOpen={() => {
              handleDataSubmitForPreview(
                formikProps.values,
                shumaId,
                formikProps.dirty
              )
            }}
          />
          {loading ? <SpinnerContainer/> : (
            <Form_6View
              {...formikProps}
              shumaID={shumaId}
              stepID={stepId}
              prevPagePath={P.GET_FORM_BY_ID_AND_STEP(shumaId, 1)}
              nextPagePath={P.GET_FORM_BY_ID_AND_STEP(shumaId, 5)}
              options={{
                buildPermitReasonOptions,
                supervisionFileOptions,
                exceedUsePermitOptions,
                exceedBuildingOptions,
                form4Options,
                completeCertOptions,
                dangerousStructureOptions
              }}
              fileUploaded={{
                commonHousePlanFileUploaded,
                permitBlueprintFileUploaded,
                plansInEffectFileUploaded,
                licensingDocumentsFileUploaded,
                houseSharedDocumentsFileUploaded,
                form4FileUploaded,
                completeCertFileUploaded,
                dangerousStructureFileUploaded,
                houseSharedOrderFileUploaded,
                propertyLegalDefinitionFileUploaded
              }}
              onFileSelected={handleFileUpload(formikProps)}
              fileTypeIds={fileTypeIds}
              showUploadingFilePopUp={showUploadingFilePopUp}
              progress={progress}
              stepData={stepData}
              shumaTypeID={shumaTypeID}
              fileSelectOptions={getFileSelectOptions(formikProps)}
              filesListData={Boolean(filesListData) ? filesListData : []}
            />
          )}
        </>
      )}
    </Formik>
  );
};

export default Form_6;